// src/features/users/components/UserActions.tsx

import React, { useState } from 'react';
import {
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  CheckCircleOutline,
  SwitchAccountRounded,
} from '@mui/icons-material';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { getCurrentUserId, getCurrentRole } from '@/utils/auth';
import { canImpersonate, startImpersonation } from '@/utils/impersonation';
import type { User } from '../types';

interface UserActionsProps {
  record: User;
}

/**
 * Row actions for Users list
 * Shows menu with Edit, Delete, and Activate options based on permissions and user state
 */
const UserActions: React.FC<UserActionsProps> = ({ record }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const { canEdit, canDelete, canManageSuperAdmins } = useUserPermissions();

  // Non-SuperAdmin users cannot edit/delete their own account
  const currentUserId = getCurrentUserId();
  const currentRole = getCurrentRole();
  const isSelf = currentUserId === record.id;
  const isSuperAdmin = currentRole === 'SuperAdmin' || currentRole === 'Admin';
  const isTargetSuperAdmin = record.role === 'SuperAdmin';

  // Block actions on SuperAdmin targets if current user can't manage SuperAdmins
  const superAdminBlocked = isTargetSuperAdmin && !canManageSuperAdmins;
  const canEditThis = canEdit && !(isSelf && !isSuperAdmin) && !superAdminBlocked;
  const canDeleteThis = canDelete && !(isSelf && !isSuperAdmin) && !superAdminBlocked;
  const canImpersonateThis = canImpersonate(currentRole, currentUserId, record);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const open = Boolean(anchorEl);

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  // Action handlers
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    redirect('edit', 'users', record.id);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleImpersonate = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    startImpersonation({
      sub: record.id,
      displayName: record.name || record.email,
      role: record.role,
    });
    // Full reload from the root: the effective role changed, so every
    // mounted admin view is stale.
    window.location.hash = '#/';
    window.location.reload();
  };

  const handleActivate = async (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setIsActivating(true);

    try {
      await dataProvider.update('users', {
        id: `${record.id}/activate`,
        data: {},
        previousData: record,
      });

      notify('resources.users.messages.activate_success', { type: 'success' });
      refresh();
    } catch (error: any) {
      const errorCode = error?.body?.error?.code;
      const errorMessage = errorCode === 'ACCOUNT_DISABLED'
        ? translate('resources.users.messages.account_disabled')
        : translate('resources.users.messages.activate_error');
      notify(errorMessage, { type: 'error' });
      if (errorCode === 'ACCOUNT_DISABLED') refresh();
    } finally {
      setIsActivating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      await dataProvider.delete('users', {
        id: record.id,
        previousData: record,
      });

      notify('resources.users.messages.deactivate_success', { type: 'success' });
      handleDeleteDialogClose();
      refresh();
    } catch (error: any) {
      const errorCode = error?.body?.error?.code;
      let errorMessage = translate('resources.users.messages.delete_error');

      // Handle specific error codes
      if (errorCode === 'USER_NOT_FOUND') {
        errorMessage = translate('resources.users.messages.user_not_found');
      } else if (errorCode === 'SELF_DEACTIVATION') {
        errorMessage = translate('resources.users.messages.self_deactivation');
      }

      notify(errorMessage, { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  // Hide the entire menu if no actions are available
  const hasAnyAction = canEditThis || canDeleteThis || canImpersonateThis;
  if (!hasAnyAction) return null;

  const isOperationInProgress = isActivating || isDeleting;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isOperationInProgress ? (
          <CircularProgress size={20} sx={{ m: '4px' }} />
        ) : (
          <Tooltip title={translate('ra.action.menu')}>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ color: 'action.active' }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(e: any) => handleClose(e)}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': 'user-actions-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Edit */}
        {canEditThis && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}

        {/* Impersonate (Admin/SuperAdmin acting as a strictly lower-role, active user) */}
        {canImpersonateThis && (
          <MenuItem onClick={handleImpersonate}>
            <ListItemIcon>
              <SwitchAccountRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.users.actions.impersonate')}</ListItemText>
          </MenuItem>
        )}

        {/* Activate (only for inactive users) */}
        {canEditThis && !record.isActive && (
          <MenuItem
            onClick={handleActivate}
            disabled={isActivating}
            sx={{
              color: 'success.main',
              '&:hover': {
                backgroundColor: 'success.light',
              },
            }}
          >
            <ListItemIcon>
              <CheckCircleOutline fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{translate('resources.users.actions.activate')}</ListItemText>
          </MenuItem>
        )}

        {/* Delete (deactivate) - only for active users */}
        {canDeleteThis && record.isActive && (
          <MenuItem
            onClick={handleDeleteClick}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
              },
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{translate('resources.users.actions.deactivate')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          {translate('resources.users.messages.deactivate_confirm')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('resources.users.messages.deactivate_content', {
              name: record.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting
              ? translate('ra.action.loading')
              : translate('resources.users.actions.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserActions;
